/**
 * BerichtsThread — Chat-basierter Berichts-Thread zu einem Vorgang
 * (Serviceticket oder KVP-Aufgabe), gespeist vom zentralen Nachrichten-
 * Service (rmnc-nachrichten). Ersetzt den E-Mail-Berichtsversand:
 * Zwischen-/Endberichte und Kommentare laufen als Thread, der auch in der
 * Portal-Inbox des Empfängers auftaucht; die Checkbox „auch per E-Mail
 * senden" löst parallel eine Mail an die übrigen Teilnehmer aus.
 *
 * Aktualisierung per Polling (30 s, Plattform-Muster) — kein WebSocket.
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Badge, Button, Select, Textarea } from "@/components/ui"
import { useToast, fehlerNachricht } from "@/lib/toast"
import {
  NACHRICHTEN_ENABLED,
  fetchNachrichten,
  lookupThread,
  markiereGelesen,
  posteNachricht,
  type NachrichtTyp,
} from "@/lib/nachrichtenApi"

const POLL_MS = 30_000

const TYP_LABEL: Record<string, string> = {
  kommentar: "Kommentar",
  zwischenbericht: "Zwischenbericht",
  abschlussbericht: "Endbericht",
  system: "Hinweis",
}

const TYP_TONE: Record<string, "slate" | "orange" | "green"> = {
  kommentar: "slate",
  zwischenbericht: "orange",
  abschlussbericht: "green",
  system: "slate",
}

function zeitpunkt(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function BerichtsThread({
  objektTyp,
  objektId,
}: {
  objektTyp: "ticket" | "kvp_aufgabe"
  objektId: string
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [typ, setTyp] = useState<NachrichtTyp>("kommentar")
  const [body, setBody] = useState("")
  const [auchPerEmail, setAuchPerEmail] = useState(false)

  const threadQuery = useQuery({
    queryKey: ["nachrichten-thread", objektTyp, objektId],
    queryFn: () => lookupThread(objektTyp, objektId),
    enabled: NACHRICHTEN_ENABLED,
    refetchInterval: POLL_MS,
  })
  const thread = threadQuery.data

  const nachrichtenQuery = useQuery({
    queryKey: ["nachrichten", thread?.id],
    queryFn: () => fetchNachrichten(thread!.id),
    enabled: NACHRICHTEN_ENABLED && Boolean(thread),
    refetchInterval: POLL_MS,
  })
  const nachrichten = nachrichtenQuery.data ?? []

  // Beim Öffnen (und bei neuen Nachrichten) den Lesestand setzen —
  // best-effort, Fehler hier sind unkritisch.
  useEffect(() => {
    if (thread?.id && nachrichten.length > 0) {
      void markiereGelesen(thread.id).catch(() => undefined)
    }
  }, [thread?.id, nachrichten.length])

  const senden = useMutation({
    mutationFn: () => posteNachricht(thread!.id, { typ, body: body.trim(), auchPerEmail }),
    onSuccess: (ergebnis) => {
      setBody("")
      setAuchPerEmail(false)
      setTyp("kommentar")
      void queryClient.invalidateQueries({ queryKey: ["nachrichten", thread!.id] })
      void queryClient.invalidateQueries({ queryKey: ["nachrichten-thread", objektTyp, objektId] })
      if (ergebnis.emailErgebnis === "gesendet") {
        toast.success("Nachricht gespeichert, E-Mail an die Teilnehmer verschickt.")
      } else if (ergebnis.emailErgebnis === "keine_empfaenger") {
        toast.info("Nachricht gespeichert — kein Teilnehmer mit E-Mail-Adresse.")
      } else if (ergebnis.emailErgebnis && ergebnis.emailErgebnis !== "gesendet") {
        toast.info("Nachricht gespeichert; E-Mail-Versand: " + ergebnis.emailErgebnis)
      } else {
        toast.success("Nachricht gespeichert.")
      }
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Nachricht konnte nicht gesendet werden.")),
  })

  if (!NACHRICHTEN_ENABLED) return null

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Berichte &amp; Kommunikation</h2>
        {thread?.status === "abgeschlossen" && <Badge tone="green">abgeschlossen</Badge>}
      </div>

      {threadQuery.isLoading && (
        <p className="py-4 text-sm text-slate-400">Lade Kommunikations-Verlauf …</p>
      )}

      {!threadQuery.isLoading && !thread && (
        <p className="py-4 text-sm text-slate-400">
          Für diesen Vorgang existiert noch kein Kommunikations-Thread. Er wird beim
          nächsten Speichern des Vorgangs automatisch angelegt.
        </p>
      )}

      {thread && (
        <>
          <ul className="mb-4 max-h-96 space-y-3 overflow-y-auto pr-1">
            {nachrichten.map((n) => (
              <li key={n.id} className="rounded-md border border-slate-200 p-3">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <Badge tone={TYP_TONE[n.typ] ?? "slate"}>{TYP_LABEL[n.typ] ?? n.typ}</Badge>
                  <span className="font-medium text-slate-700">
                    {n.verfasserName || "System"}
                  </span>
                  <span>{zeitpunkt(n.erstelltAm)}</span>
                  {n.emailAngefordert && <span title="auch per E-Mail verschickt">📧</span>}
                </div>
                <p className="whitespace-pre-wrap text-sm text-slate-900">{n.body}</p>
              </li>
            ))}
            {nachrichten.length === 0 && (
              <li className="py-4 text-center text-sm text-slate-400">
                Noch keine Nachrichten — der erste Bericht startet den Verlauf.
              </li>
            )}
          </ul>

          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (body.trim()) senden.mutate()
            }}
          >
            <div className="flex items-center gap-2">
              <Select
                value={typ}
                onChange={(e) => setTyp(e.target.value as NachrichtTyp)}
                className="w-auto"
                aria-label="Nachrichtentyp"
              >
                <option value="kommentar">Kommentar</option>
                <option value="zwischenbericht">Zwischenbericht</option>
                <option value="abschlussbericht">Endbericht</option>
              </Select>
              <label className="flex items-center gap-1.5 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={auchPerEmail}
                  onChange={(e) => setAuchPerEmail(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                />
                auch per E-Mail senden
              </label>
            </div>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder={
                typ === "kommentar"
                  ? "Nachricht an die Beteiligten…"
                  : `${TYP_LABEL[typ]} verfassen…`
              }
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Der Verlauf erscheint bei allen Beteiligten auch in der Portal-Inbox.
                {typ === "abschlussbericht" && " Ein Endbericht schließt den Thread ab."}
              </p>
              <Button type="submit" disabled={!body.trim() || senden.isPending}>
                {senden.isPending ? "Senden…" : "Senden"}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
